/**
 * Spartan Fitness - showroom-based variant pricing
 *
 * Products with an option named "Location" / "Showroom" / "Store" (see
 * snippets/spartan-store-pricing.liquid) get that option pre-selected from the header
 * store widget (spartan-nearest-store.js), on the product page and on product cards.
 *
 * Option values are free text. A value matches the shopper's store when it mentions:
 *   - the store's city            "RED DEER", "Sherwood Park", "Calgary"          (strongest)
 *   - the store's province        "ON & AB", "BC/NS/PEI", "SK/NEWFOUNDLAND", "Alberta"
 *     ("Atlantic" counts for NS, NB, PE, NL; "Maritimes" for NS, NB, PE)
 * and does NOT list the city/province after "excludes", "excluding", "except", "not in",
 * "other than":  "ON & AB STORE PICKUP (excludes Red Deer - Sherwood Park - Newmarket)".
 * Two-letter codes must be written in CAPITALS (so the word "on" is not Ontario).
 * Values about shipping ("Shipped to an Address") are never auto-selected.
 * A city match beats a province match; on a tie the first value wins. If nothing matches,
 * the first non-shipping value is used.
 *
 * The selector is hidden when every value is a store (price follows the header store, with
 * a "Change store" link); it stays visible when a value is about shipping, so shoppers can
 * still choose it. Once a shopper picks a value by hand, only an explicit store change in
 * the header overrides it.
 */
(function () {
  if (window.SpartanStorePricing) return;

  const PROVINCES = {
    AB: ['alberta'],
    BC: ['british columbia'],
    MB: ['manitoba'],
    NB: ['new brunswick', 'atlantic', 'maritime', 'maritimes'],
    NL: ['newfoundland', 'nfld', 'labrador', 'atlantic'],
    NS: ['nova scotia', 'atlantic', 'maritime', 'maritimes'],
    NT: ['northwest territories'],
    NU: ['nunavut'],
    ON: ['ontario'],
    PE: ['pei', 'p e i', 'prince edward island', 'atlantic', 'maritime', 'maritimes'],
    QC: ['quebec', 'pq'],
    SK: ['saskatchewan', 'sask'],
    YT: ['yukon'],
  };
  const CODE_ALIASES = { PEI: 'PE', PQ: 'QC', NF: 'NL', NFLD: 'NL' };

  // Extra place names that should count for a store's city.
  const CITY_ALIASES = {
    ancaster: ['hamilton'],
    dartmouth: ['halifax'],
    'st john s': ['st johns', 'saint john s', 'saint johns'],
  };

  const EXCLUDE_RE = /\b(?:excl\w*|except(?:ing)?|not\s+(?:incl\w*|available|valid|at|in|for)|other\s+than)\b[^)\]]*/gi;
  const SHIPPING_RE = /\b(?:ship(?:ped|ping|s)?|deliver\w*|courier)\b/i;
  const PICKUP_RE = /\bpick\s*-?\s*up\b|\bin[\s-]*store\b|\bshowroom\b/i;

  function norm(s) {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  // 30000 cents -> "$300"; 29950 -> "$299.50"
  function formatSaving(cents) {
    const d = cents / 100;
    return '$' + (d % 1 ? d.toFixed(2) : String(d)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function hasPhrase(text, phrase) {
    // text and phrase both normalised; whole-word match
    return phrase && (' ' + text + ' ').indexOf(' ' + phrase + ' ') > -1;
  }

  function provinceCode(p) {
    const raw = String(p || '').trim();
    const up = raw.toUpperCase().replace(/[^A-Z]/g, '');
    if (PROVINCES[up]) return up;
    if (CODE_ALIASES[up]) return CODE_ALIASES[up];
    const n = norm(raw);
    return Object.keys(PROVINCES).find((c) => PROVINCES[c].some((name) => n === name)) || null;
  }

  function mentionsProvince(rawText, code) {
    if (!code) return false;
    const codes = [code].concat(Object.keys(CODE_ALIASES).filter((a) => CODE_ALIASES[a] === code));
    // codes: capitals only, bounded by non-letters
    if (codes.some((c) => new RegExp('(^|[^A-Za-z])' + c + '(?![A-Za-z])').test(rawText))) return true;
    const n = norm(rawText);
    return PROVINCES[code].some((name) => hasPhrase(n, name));
  }

  // "Sherwood Park / Edmonton" -> ["sherwood park edmonton", "sherwood park", "edmonton"] (+ aliases)
  function cityNames(city) {
    const names = [norm(city)].concat(
      String(city || '').split(/[\/,&+|]|\s+-\s+|\s+or\s+/i).map(norm)
    ).filter(Boolean);
    names.slice().forEach((n) => { (CITY_ALIASES[n] || []).forEach((a) => names.push(a)); });
    return [...new Set(names)];
  }

  function mentionsCity(rawText, city) {
    const n = norm(rawText);
    return cityNames(city).some((name) => hasPhrase(n, name));
  }

  function isShippingValue(value) {
    return SHIPPING_RE.test(value) && !PICKUP_RE.test(value);
  }

  /** Score a value for a store: -1 excluded/never, 0 no match, 2 province, 3 city. */
  function scoreValue(value, store) {
    const text = String(value);
    if (isShippingValue(text)) return -1;
    const excluded = (text.match(EXCLUDE_RE) || []).join(' ');
    const positive = text.replace(EXCLUDE_RE, ' ');
    const code = provinceCode(store.province);

    if (excluded && (mentionsCity(excluded, store.city) || mentionsProvince(excluded, code))) return -1;
    if (mentionsCity(positive, store.city)) return 3;
    if (mentionsProvince(positive, code)) return 2;
    return 0;
  }

  function pickValue(values, store) {
    let best = null;
    let bestScore = 0;
    values.forEach((v) => {
      const s = scoreValue(v, store);
      if (s > bestScore) { best = v; bestScore = s; }
    });
    if (best !== null) return { value: best, matched: true };
    const fallback = values.find((v) => !isShippingValue(v));
    return { value: fallback !== undefined ? fallback : values[0], matched: false };
  }

  /* ---------------- current store state ---------------- */

  const state = { store: null, final: false, explicit: false };
  const listeners = new Set();

  function widget() { return window.spartanStoreWidgetInstance; }

  function readWidget() {
    const w = widget();
    if (w && w.currentStore) {
      state.store = w.currentStore;
      state.final = !!w.isOverride || w.currentDistance != null;
    }
  }

  function notify() {
    listeners.forEach((fn) => {
      try { fn(state); } catch (e) { console.warn('[Spartan store pricing]', e); }
    });
  }

  let lastStoreId = null;
  document.addEventListener('spartan:store_changed', (e) => {
    if (!e.detail || !e.detail.store) return;
    const w = widget();
    const override = !!(w && w.isOverride);
    // "explicit" = the shopper just picked this store in the header
    state.explicit = override && lastStoreId !== null && e.detail.store.id !== lastStoreId;
    lastStoreId = e.detail.store.id;
    state.store = e.detail.store;
    state.final = override || e.detail.distance != null;
    notify();
  });

  function openStorePicker() {
    const trigger = document.querySelector('#spartanNearestStoreWidget .spartan-nearest-store-trigger');
    if (!trigger) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => trigger.click(), 250);
  }

  /* ---------------- product page ---------------- */

  class SpartanStorePrice extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.optionIndex = parseInt(this.dataset.optionIndex, 10);
      this.section = this.closest('.js-product');
      if (!this.section || isNaN(this.optionIndex)) return;

      this.userPicked = false;
      this.priceWrap = this.section.querySelector('.product-info__price');
      // The price is never hidden: the default price shows at once and switches to the
      // shopper's store price as soon as their store is known.

      customElements.whenDefined('variant-picker').then(() => {
        this.picker = this.section.querySelector('variant-picker');
        if (!this.picker) return this.reveal();
        this.selector = this.picker.querySelectorAll('.option-selector:not(.option-selector--custom)')[this.optionIndex];
        if (!this.selector) return this.reveal();

        this.selector.addEventListener('change', () => {
          if (this.applying) return;
          this.userPicked = true;
          this.setNote(false);
        });

        this.buildNote();
        if (!this.values().some(isShippingValue)) this.selector.classList.add('sp-store-option-hidden');
        this.onState = (s) => this.apply(s.explicit);
        listeners.add(this.onState);
        readWidget();
        this.apply(false);
      });
    }

    disconnectedCallback() {
      if (this.onState) listeners.delete(this.onState);
      clearTimeout(this.revealTimer);
    }

    buildNote() {
      this.note = document.createElement('p');
      this.note.className = 'sp-store-price-note';
      this.note.hidden = true;
      this.note.innerHTML = '<span class="sp-store-price-note__text"></span> \u00b7 ' +
        '<button type="button" class="sp-store-price-note__change link">Change store</button>';
      this.note.querySelector('button').addEventListener('click', openStorePicker);
      this.selector.parentNode.insertBefore(this.note, this.selector.nextSibling);
      if (!state.store) {
        this.note.querySelector('.sp-store-price-note__text').textContent = 'Prices may vary by store';
        this.note.querySelector('button').textContent = 'Choose your store';
        this.note.hidden = false;
      }
    }

    values() {
      if (this.selector.dataset.selectorType === 'dropdown') {
        return Array.from(this.selector.querySelectorAll('.custom-select__option'))
          .map((li) => li.dataset.value).filter((v) => v);
      }
      return Array.from(this.selector.querySelectorAll('input.js-option')).map((i) => i.value);
    }

    select(value) {
      this.applying = true;
      try {
        if (this.selector.dataset.selectorType === 'dropdown') {
          const cs = this.selector.querySelector('custom-select');
          const li = Array.from(this.selector.querySelectorAll('.custom-select__option')).find((o) => o.dataset.value === value);
          if (cs && li && li.getAttribute('aria-selected') !== 'true' && typeof cs.selectOption === 'function') {
            cs.selectOption(li);
          }
        } else {
          const input = Array.from(this.selector.querySelectorAll('input.js-option')).find((i) => i.value === value);
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } finally {
        this.applying = false;
      }
    }

    apply(explicit) {
      if (!state.store) return;
      if (this.userPicked && !explicit) { this.setNote(false); this.reveal(); return; }
      if (explicit) this.userPicked = false;

      const pick = pickValue(this.values(), state.store);
      if (pick.value) this.select(pick.value);

      this.setNote(pick.matched);
      if (state.final) this.reveal();
    }

    setNote(matched) {
      if (!this.note || !state.store) return;
      this.note.querySelector('button').textContent = 'Change store';
      this.note.querySelector('.sp-store-price-note__text').textContent = matched
        ? 'Price shown for your ' + state.store.city + ' store'
        : 'Your store: ' + state.store.city;
      this.note.hidden = false;
    }

    reveal() {
      clearTimeout(this.revealTimer);
      if (this.priceWrap) this.priceWrap.classList.remove('sp-store-pending');
    }
  }

  /* ---------------- product cards ---------------- */

  class SpartanStoreCardPrice extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.rows = JSON.parse(this.dataset.prices || '[]'); } catch (e) { this.rows = []; }
      if (!this.rows.length) return;
      this.onState = () => this.apply();
      listeners.add(this.onState);
      readWidget();
      this.apply();
    }

    disconnectedCallback() { if (this.onState) listeners.delete(this.onState); }

    apply() {
      if (!state.store) return;
      const values = [...new Set(this.rows.map((r) => r.v))];
      const { value } = pickValue(values, state.store);
      const rows = this.rows.filter((r) => r.v === value);
      if (!rows.length) return;
      const min = rows.reduce((a, b) => (b.p < a.p ? b : a));
      const varies = rows.some((r) => r.p !== min.p);

      const current = this.querySelector('.price__current');
      if (current) current.innerHTML = min.f;
      const from = this.querySelector('.price__from');
      if (from) from.hidden = !varies;
      const was = this.querySelector('.price__was');
      if (was) was.innerHTML = min.c > min.p ? min.cf : '';
      const price = this.querySelector('.price');
      if (price) price.classList.toggle('price--on-sale', min.c > min.p);
      this.applyBadges(min);
    }

    // Keep the card's sale badge in step with the store price shown.
    applyBadges(row) {
      const card = this.closest('.product-block, .sf-card');
      if (!card) return;
      const onSale = row.c > row.p;
      card.querySelectorAll('.product-label--sale').forEach((label) => {
        const span = label.querySelector('span');
        if (onSale && span && span.textContent.indexOf('%') > -1) {
          span.textContent = Math.round((1 - row.p / row.c) * 100) + '% off';
        }
        label.hidden = !onSale;
      });
      card.querySelectorAll('[data-sp-save]').forEach((save) => {
        if (onSale) save.textContent = 'Save ' + formatSaving(row.c - row.p);
        save.hidden = !onSale;
      });
    }
  }

  customElements.define('spartan-store-price', SpartanStorePrice);
  customElements.define('spartan-store-card-price', SpartanStoreCardPrice);

  window.SpartanStorePricing = { scoreValue, pickValue, provinceCode, state };
})();
